import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  selectPotentialConsequences,
  selectPotentialConsequencesError,
  selectPotentialConsequencesLoading,
} from '../../../store/selectors/potential-consequence.selector';
import { Store } from '@ngrx/store';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import * as potentialConsequenceActions from '../../../store/actions/potential-consequence.actions';
import { PotentialConsequence } from '../../../models/potential-consequence.model';
import { MessageService } from 'primeng/api';
import { UpdatePotentialConsequenceRequest } from '../../../models/requests/update-potential-consequence-request';
import { CreatePotentialConsequenceRequest } from '../../../models/requests/create-potential-consequence-request';
import { GLOBAL_TOAST } from '../../../shared/constants/global-key.constans';

@Component({
  selector: 'app-potential-consequence',
  templateUrl: './potential-consequence.component.html',
  styleUrl: './potential-consequence.component.scss',
  standalone: false,
})
export class PotentialConsequenceComponent
  implements OnInit, OnChanges, OnDestroy
{
  constructor(
    private store: Store,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.subscribeToStore();
    if (this.ruleUid) {
      this.loadInitialData();
    }
  }

  @Input() ruleUid: string | undefined = undefined;
  potentialConsequences: PotentialConsequence[] | undefined = undefined;
  loading$ = this.store.select(selectPotentialConsequencesLoading);
  error$ = this.store.select(selectPotentialConsequencesError);
  private destroy$ = new Subject<void>();

  showEditModal = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ruleUid']) {
      const newRuleUid = changes['ruleUid'].currentValue;
      if (newRuleUid) {
        this.ruleUid = newRuleUid;
        this.loadInitialData();
      }
    }
  }

  get sortedPotentialConsequences(): PotentialConsequence[] {
    if (!this.potentialConsequences) {
      return [];
    }

    return [...this.potentialConsequences].sort((a, b) => {
      return (a.description || '').localeCompare(b.description || '');
    });
  }

  private loadInitialData(): void {
    this.store.dispatch(
      potentialConsequenceActions.loadPotentialConsequences({
        ruleUid: this.ruleUid,
      }),
    );
  }

  private subscribeToStore(): void {
    combineLatest([this.store.select(selectPotentialConsequences)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([potentialConsequences]) => {
        this.potentialConsequences = potentialConsequences;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModalSubmit(result: any): void {
    this.handleModalResult(result);
    this.showEditModal = false;
  }

  private async handleModalResult(result: any): Promise<void> {
    const { toCreate, toUpdate, toDelete } = result;

    if (toDelete && toDelete.length > 0) {
      this.store.dispatch(
        potentialConsequenceActions.deleteMultiplePotentialConsequences({
          uids: toDelete,
        }),
      );
    }

    if (toUpdate && toUpdate.length > 0) {
      const updateRequests: UpdatePotentialConsequenceRequest[] = toUpdate.map(
        (consequence: any) => ({
          uid: consequence.uid,
          ruleFK: this.ruleUid!,
          description: consequence.description,
        }),
      );
      this.store.dispatch(
        potentialConsequenceActions.updateMultiplePotentialConsequences({
          requests: updateRequests,
        }),
      );
    }

    if (toCreate && toCreate.length > 0) {
      const createRequests: CreatePotentialConsequenceRequest[] = toCreate.map(
        (consequence: any) => ({
          description: consequence.description,
          ruleFK: this.ruleUid!,
        }),
      );

      this.store.dispatch(
        potentialConsequenceActions.createMultiplePotentialConsequences({
          requests: createRequests,
        }),
      );
    }

    this.messageService.add({
      key: GLOBAL_TOAST,
      severity: 'success',
      summary: 'Succès',
      detail: 'Les modifications ont été envoyées.',
    });
  }

  onModalCancel(): void {
    this.showEditModal = false;
  }

  openEditModal(): void {
    this.showEditModal = true;
  }
}
